import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Badge,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { uploadMedia, getMedias, deleteMedia, getMediaLinks } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { SavingOverlay } from '../../core/public/SavingOverlay';
import MediaLightbox from '../../medias/public/MediaLightbox';
import { Link } from '@mui/material';
import { isVideoMimeOrExtension } from '../../medias/public/mediaMime';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const MediasPage = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadDialog, setUploadDialog] = useState({ open: false });
  const [previewDialog, setPreviewDialog] = useState({ open: false, media: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, media: null, links: [] });
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [metadataKey, setMetadataKey] = useState('');
  const [metadataValue, setMetadataValue] = useState('');

  const loadMedias = async () => {
    try {
      setLoading(true);
      const response = await getMedias();
      setMedias(response.data.medias || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedias();
  }, []);

  const handleUploadClick = () => {
    setUploadDialog({ open: true });
    setSelectedFile(null);
    setMetadataKey('');
    setMetadataValue('');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: translate('media.select_file'),
        severity: 'error',
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (metadataKey && metadataValue) {
        formData.append('metadata_key', metadataKey);
        formData.append('metadata_value', metadataValue);
      }

      await uploadMedia(formData);
      setSnackbar({
        open: true,
        message: translate('media.uploaded'),
        severity: 'success',
      });
      setUploadDialog({ open: false });
      setSelectedFile(null);
      setMetadataKey('');
      setMetadataValue('');
      loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = (media) => {
    setPreviewDialog({ open: true, media });
  };

  const handleDownload = (media) => {
    window.open(`${API_URL}${media.file_path}`, '_blank');
  };

  const handleDeleteClick = async (media) => {
    try {
      const response = await getMediaLinks(media.id);
      const links = response.data.links || [];
      setDeleteDialog({ open: true, media, links });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    const { media } = deleteDialog;
    setDeleteDialog({ open: false, media: null, links: [] });

    try {
      await deleteMedia(media.id);
      setSnackbar({
        open: true,
        message: translate('media.deleted'),
        severity: 'success',
      });
      loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, media: null, links: [] });
  };

  const isImage = (mimeType) => {
    return mimeType && mimeType.startsWith('image/');
  };

  const canPreviewInDialog = (media) =>
    isImage(media.mime_type) ||
    isVideoMimeOrExtension(media.mime_type, media.filename);

  const getLinkCount = (media) => {
    return media.metadata ? media.metadata.length : 0;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleUploadClick}
        >
          {translate('common.upload')}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : medias.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {translate('media.no_medias')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {medias.map((media) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={media.id}>
              <Card>
                {isImage(media.mime_type) ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={`${API_URL}${media.file_path}`}
                    alt={media.filename}
                    sx={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => handlePreview(media)}
                  />
                ) : isVideoMimeOrExtension(media.mime_type, media.filename) ? (
                  <CardMedia
                    component="video"
                    height="200"
                    src={`${API_URL}${media.file_path}`}
                    muted
                    playsInline
                    preload="metadata"
                    sx={{ objectFit: 'cover', cursor: 'pointer', display: 'block' }}
                    onClick={() => handlePreview(media)}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {media.filename}
                    </Typography>
                  </Box>
                )}
                <CardContent>
                  <Typography variant="body2" noWrap title={media.filename}>
                    {media.filename}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(media.file_size / 1024).toFixed(2)} KB
                  </Typography>
                  {getLinkCount(media) > 0 && (
                    <Badge badgeContent={getLinkCount(media)} color="primary" sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {translate('media.linked')}
                      </Typography>
                    </Badge>
                  )}
                </CardContent>
                <CardActions sx={{ gap: 0.5 }}>
                  {canPreviewInDialog(media) && (
                    <Tooltip title={translate('media.preview') || 'Visualizar'}>
                      <IconButton sx={{ p: '10px' }} onClick={() => handlePreview(media)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={translate('media.download') || 'Baixar'}>
                    <IconButton sx={{ p: '10px' }} onClick={() => handleDownload(media)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate('common.delete')}>
                    <IconButton sx={{ p: '10px' }} color="error" onClick={() => handleDeleteClick(media)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={uploadDialog.open} onClose={() => setUploadDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('media.upload')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*,application/pdf,video/*"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
              key={uploadDialog.open ? 'file-input-open' : 'file-input-closed'}
            />
            <label htmlFor="file-upload">
              <Button variant="outlined" component="span" fullWidth>
                {selectedFile ? selectedFile.name : translate('media.select_file')}
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <TextField
              label={translate('media.metadata_key')}
              fullWidth
              value={metadataKey}
              onChange={(e) => setMetadataKey(e.target.value)}
              placeholder="product, sku, service"
              sx={{ mb: 2 }}
            />
            <TextField
              label={translate('media.metadata_value')}
              fullWidth
              value={metadataValue}
              onChange={(e) => setMetadataValue(e.target.value)}
              placeholder="ID da entidade"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog({ open: false })}>
            {translate('common.cancel')}
          </Button>
          <Button onClick={handleUpload} variant="contained" disabled={uploading || !selectedFile}>
            {uploading ? translate('common.uploading') : translate('common.upload')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carousel order = full `medias` list (same as grid/API order) for parity with MediaGallery. */}
      <MediaLightbox
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, media: null })}
        items={medias}
        activeMedia={previewDialog.media}
        onNavigate={(media) => setPreviewDialog({ open: true, media })}
        apiBaseUrl={API_URL}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{translate('media.confirm_delete')}</DialogTitle>
        <DialogContent>
          {deleteDialog.links.length > 0 ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {translate('media.delete_with_links')}
              </Typography>
              {deleteDialog.links.map((link, index) => {
                const linkValue = link.value;
                const isObject = typeof linkValue === 'object' && linkValue !== null;
                
                let url = null;
                let displayValue = '';
                
                if (isObject) {
                  // Se é objeto, verifica se tem URL ou constrói baseado no tipo
                  url = linkValue.url || null;
                  const entityId = linkValue.id || null;
                  
                  // Se não tem URL mas tem ID, constrói a URL baseado no tipo
                  if (!url && entityId) {
                    if (link.key === 'product_image') {
                      url = `/products/${entityId}/edit`;
                    } else if (link.key === 'sku_image') {
                      url = `/products/${entityId}/edit`; // Ajustar quando tiver página de SKU
                    } else if (link.key === 'service_image') {
                      url = `/services/${entityId}`; // Ajustar quando tiver página de serviço
                    }
                  }
                  
                  displayValue = `${link.key}: ${entityId || JSON.stringify(linkValue)}`;
                } else {
                  // Se é string, tenta construir URL baseado no tipo
                  if (link.key === 'product_image') {
                    url = `/products/${linkValue}/edit`;
                  } else if (link.key === 'sku_image') {
                    url = `/products/${linkValue}/edit`;
                  } else if (link.key === 'service_image') {
                    url = `/services/${linkValue}`;
                  }
                  
                  displayValue = `${link.key}: ${linkValue}`;
                }
                
                return (
                  <Box key={index} sx={{ mb: 1 }}>
                    {url ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          handleDeleteCancel();
                          navigate(url);
                        }}
                        sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'primary.main' }}
                      >
                        {displayValue}
                      </Link>
                    ) : (
                      <Typography variant="body2">{displayValue}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2">
              {translate('media.confirm_delete_message')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            {translate('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {translate('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <SavingOverlay open={uploading} messageKey="common.uploading" />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MediasPage;
